import React, { useState, useReducer, useCallback, useEffect } from 'react';
import classNames from 'classnames';
import { I } from '../../../common/v5/config';
import { TableHeader } from '../groups/groups';
import KanbanBoard from '../groups/draggables';
import Button from '../../../reactcomponents/Button';
import { TableIconicButton } from '../../../reactcomponents/Form';
import { ModalBox } from '../../../reactcomponents/Modal';
import Helper from '../../../reactcomponents/Helper';

// Reducer for managing kanban board state
const groupReducer = (state, action) => {
    switch (action.type) {
        case 'SET_DATA':
            return action.payload;
        case 'UPDATE_COLUMNS':
            return {
                ...state,
                ...action.payload
            };
        case 'MOVE_ITEM': {
            const { source, destination, draggableId } = action.payload;
            
            const sourceColumn = state[source.droppableId];
            const destColumn = state[destination.droppableId];
            
            // Remove from source
            const sourceItems = Array.from(sourceColumn.list);
            const [draggedItem] = sourceItems.splice(source.index, 1);
            
            // Add to destination
            const destItems = Array.from(destColumn.list);
            destItems.splice(destination.index, 0, draggedItem);
            
            return {
                ...state,
                [source.droppableId]: {
                    ...sourceColumn,
                    list: sourceItems
                },
                [destination.droppableId]: {
                    ...destColumn,
                    list: destItems
                }
            };
        }
        case 'MOVE_MULTI': {
            const { source, destination, items } = action.payload;
            
            const sourceColumn = state[source.droppableId];
            const destColumn = state[destination.droppableId];
            
            // Remove selected items from all columns
            const newState = { ...state };
            Object.keys(newState).forEach(columnId => {
                newState[columnId] = {
                    ...newState[columnId],
                    list: newState[columnId].list.filter(item => 
                        !items.some(selectedItem => selectedItem.id === item.id)
                    )
                };
            });
            
            // Add items to destination column
            const destItems = Array.from(newState[destination.droppableId].list);
            destItems.splice(destination.index, 0, ...items);
            
            return {
                ...newState,
                [destination.droppableId]: {
                    ...newState[destination.droppableId],
                    list: destItems
                }
            };
        }
        default:
            return state;
    }
};

// Upload form component
const GroupUploadForm = ({ onClose, onUpload }) => {
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);

    const handleFileChange = (e) => {
        setFile(e.target.files[0]);
    };

    const handleUpload = async () => {
        if (!file) return;
        
        setUploading(true);
        try {
            // Implementation for file upload would go here
            await onUpload(file);
            onClose();
        } catch (error) {
            console.error('Upload failed:', error);
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="group-upload-form">
            <div className="upload-section">
                <label htmlFor="groupFile" className="upload-label">
                    {I('Select CSV file')}
                </label>
                <input
                    id="groupFile"
                    type="file"
                    accept=".csv"
                    onChange={handleFileChange}
                    className="form-control"
                />
                <Helper>
                    {I('The CSV file should contain group name and agent name separated by a comma, for example:') +
                    '<br/><br/>' + I('Group name,Agent name') +
                    '<br/><br/>' + 'Support,John Doe'}
                </Helper>
            </div>
            <div className="upload-actions">
                <Button
                    type="button"
                    className="btn-grey"
                    onClick={onClose}
                    text={I('Cancel')}
                />
                <Button
                    type="button"
                    className="btn-blue"
                    onClick={handleUpload}
                    disabled={!file || uploading}
                    text={uploading ? I('Uploading...') : I('Upload')}
                />
            </div>
        </div>
    );
};

const AgentGroupsForm = ({ 
    data = {}, 
    activeId, 
    onSaveAgentGroups, 
    onCancel, 
    onGetAgentGroups,
    adminStatus,
    groupsAgentList,
    groupAgentListReady 
}) => {
    // Initialize state with default structure
    const defaultData = {
        available: {
            title: I("All Groups"),
            list: []
        },
        members: {
            title: I("Added as agents"),
            list: []
        },
        teamLeaders: {
            title: I("Added as Team Leaders"),
            list: []
        }
    };

    const [state, dispatch] = useReducer(groupReducer, data.available ? data : defaultData);
    const [checkedItems, setCheckedItems] = useState([]);
    const [selectedBoard, setSelectedBoard] = useState("");
    const [popup, setPopup] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);

    // Helper text for the interface
    const helperForAgentGroups = I("Manage agent group assignments by dragging groups between columns. Use 'All Groups' to see available groups, 'Added Groups' for current assignments, and 'Team Leader Groups' for leadership roles.");

    // Update state when data changes
    useEffect(() => {
        if (groupsAgentList && Object.keys(groupsAgentList).length > 0) {
            const formattedData = {
                available: {
                    title: I("All Groups"),
                    list: groupsAgentList.available || []
                },
                members: {
                    title: I("Added Groups"),
                    list: groupsAgentList.members || []
                },
                teamLeaders: {
                    title: I("Team Leader Groups"),
                    list: groupsAgentList.teamLeaders || []
                }
            };
            dispatch({ type: 'SET_DATA', payload: formattedData });
        }
    }, [groupsAgentList]);

    // Load data when component mounts or activeId changes
    useEffect(() => {
        if (activeId && onGetAgentGroups) {
            onGetAgentGroups(activeId);
        }
    }, [activeId, onGetAgentGroups]);

    // Drag and drop handlers
    const onDragStart = useCallback((start) => {
        const sourceList = [...state[start.source.droppableId].list];
        const [draggedItem] = sourceList.splice(start.source.index, 1);

        // Track selected items when starting drag (for multiselect)
        let currentSelected = [...checkedItems];

        // If user dragged unchecked item, include it in selection
        if (!currentSelected.some(item => item.id === draggedItem.id)) {
            currentSelected.push(draggedItem);
            setCheckedItems(currentSelected);
        }
        setSelectedBoard(start.source.droppableId);
    }, [checkedItems, state]);

    const onDragEnd = useCallback((result) => {
        const { destination, source } = result;
        
        if (!destination) {
            // Clear selections and reset
            setCheckedItems([]);
            setSelectedBoard("");
            return;
        }

        if (destination.droppableId === source.droppableId && 
            destination.index === source.index) {
            // Clear selections and reset
            setCheckedItems([]);
            setSelectedBoard("");
            return;
        }

        if (checkedItems.length >= 1) {
            // Handle multi-item move
            dispatch({
                type: 'MOVE_MULTI',
                payload: {
                    source,
                    destination,
                    items: checkedItems
                }
            });
        } else {
            // Handle single item move
            dispatch({
                type: 'MOVE_ITEM',
                payload: result
            });
        }

        // Mark as changed and reset selections
        setHasChanges(true);
        setCheckedItems([]);
        setSelectedBoard("");
    }, [checkedItems]);

    const handleItemClick = useCallback((id, column) => {
        const selectedItem = state[column].list.find(item => item.id === id);
        
        if (!selectedItem) return;
        
        // If item exists in selection, remove it
        if (checkedItems.some(item => item.id === id)) {
            const filtered = checkedItems.filter(item => item.id !== id);
            setCheckedItems(filtered);
            // If no more items selected, reset board
            if (filtered.length === 0) {
                setSelectedBoard("");
                return;
            }
        } else {
            // Add item to selection
            setCheckedItems(prevState => [...prevState, selectedItem]);
        }
        
        // Set selected board if none selected
        if (!selectedBoard) {
            setSelectedBoard(column);
        }
    }, [state, checkedItems, selectedBoard]);

    const handleClear = useCallback(() => {
        setCheckedItems([]);
        setSelectedBoard("");
    }, []);

    const handleUploadGroups = () => {
        setPopup(true);
    };

    const handleUpload = async (file) => {
        // Implementation for handling file upload
        console.log('Uploading file:', file);
        // This would call an API endpoint to process the CSV
    };

    const handleSave = useCallback(() => {
        if (onSaveAgentGroups && hasChanges) {
            const memberIds = state.members.list.map(item => item.id);
            const teamLeaderIds = state.teamLeaders.list.map(item => item.id);
            
            onSaveAgentGroups(activeId, memberIds, teamLeaderIds);
            setHasChanges(false);
        }
    }, [state, activeId, onSaveAgentGroups, hasChanges]);
    console.log('state:', state);
    const hasItemSelected = checkedItems.length > 0;
    const totalSelected = checkedItems.length;
    
    // Debug logging
    if (process.env.NODE_ENV === 'development') {
        console.log('AgentGroupsForm Debug:', {
            checkedItems,
            hasItemSelected,
            totalSelected,
            selectedBoard,
            state
        });
    }

    // Show loading state
    if (!groupAgentListReady) {
        return (
            <div className="agent-groups-loading">
                <div className="loading-spinner">
                    <i className="icon-spinner icon-spin"></i>
                    <p>{I('Loading groups...')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="agent-groups-edit">
            <TableHeader
                title={I("Agent in group")}
                helperText={helperForAgentGroups}
            >
                <div className="groups-actions">
                    <TableIconicButton
                        type='button'
                        className={classNames("btn bg-transparent btn-round")}
                        iconClass='icon-upload'
                        title={I('Upload CSV')}
                        onClick={handleUploadGroups}
                        label={I('Upload')}
                    />
                    {hasChanges && (
                        <Button
                            type='button'
                            className="btn-blue"
                            onClick={handleSave}
                            disabled={adminStatus === 'saving'}
                            text={adminStatus === 'saving' ? I('Saving...') : I('Save Changes')}
                        />
                    )}
                </div>
            </TableHeader>
            
            {popup &&
                <ModalBox
                    show={popup}
                    onClose={() => setPopup(false)}
                    className={"group-upload"}
                    headerContent={<h3>{I("Upload Groups CSV")}</h3>}
                >
                    <GroupUploadForm 
                        onClose={() => setPopup(false)}
                        onUpload={handleUpload}
                    />
                </ModalBox>
            }
            
            <div className="groups-kanban-container">
                <KanbanBoard
                    data={state}
                    checkBox={true}
                    checkedItems={checkedItems}
                    onDragStart={onDragStart}
                    onDragEnd={onDragEnd}
                    onItemClick={handleItemClick}
                    selectedBoard={selectedBoard}
                    hasItemSelected={hasItemSelected}
                    totalSelected={totalSelected}
                    itemField={"name"}
                />
            </div>
            
            <div className='board-actions'>
                <Button 
                    hide={!hasItemSelected} 
                    type="button" 
                    onClick={handleClear} 
                    className="btn-white clear-selection" 
                    title="Clear" 
                    data-qa-id="btn-clear-ticks" 
                    text={I("Clear selection")} 
                />
            </div>
        </div>
    );
};

export default AgentGroupsForm; 